import { PortalTelemetryEvent } from '../../../../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../../../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../../../../app/telemetry/validationTelemetry';
import { portalAction, portalScreenContext, trackPortalEvent } from '../../../../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../../../../app/telemetry/usePortalTelemetry';
import { Box, Button, Grid, Typography } from "@mui/material";
import AppTextareaInput from "../../../../../app/components/AppTextareaInput";
import { useForm } from "react-hook-form";
import pcsPlanFormModel from "../../../common/pcsPlanFormModel";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../app/store/configureStore";
import { updatePlannedActivityAsync } from "../../../../../app/slices/pcsPdpSlice";
import { PdpStatusEnum } from "../../../models/pdpStatusEnum";
import { toast } from "react-toastify";
import { PlannedActivity } from "../../../models/plannedActivity";
import { yupResolver } from "@hookform/resolvers/yup";
import { plannedActivitiesValidationSchema } from "../../../common/pcsPlanValidationSchema";
import {
    API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED,
    MESSAGE_PROCESSING_DETAILS
} from "../../../../../app/utils/constant";
import { getPcsEnrolmentYearsAsync } from "../../../../../app/slices/pcsEnrolmentSlice";
import { PcsEnrolmentYear } from "../../../models/pcsEnrolmentYear";
import { ApiRequestStatus } from "../../../../../app/enums/apiRequestStatus";
import LoadingComponent from "../../../../../app/layout/LoadingComponent";
import AppInformationText from "../../../../../app/components/AppInformationText";
import { PcsPdpStatus } from "../../../../../app/models/staticData/pcsPdpStatus";

export function UpdatePlannedActivities() {
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(plannedActivitiesValidationSchema)
    });

    const { control, reset, getValues, trigger } = methods;
    const { AccreditedContinuingEducationActivity, WorkBasedLearning, PracticeReview, EngagedInPracticeOfMedicine }
        = pcsPlanFormModel.formField;

    const { enrolmentYearId } = useParams<{ enrolmentYearId: string }>();

    const [canUpdatePlannedActivities, setCanUpdatePlannedActivities] = useState<boolean>();
    const [enrolmentYear, setEnrolmentYear] = useState<PcsEnrolmentYear>();
    const [currentEnrolmentYearStatus, setCurrentEnrolmentYearStatus] = useState<PcsPdpStatus>();

    const dispatch = useAppDispatch();

    const { pcsPdpStatuses } = useAppSelector(state => state.ReferenceData);
    const { enrolmentYears } = useAppSelector(state => state.pcsEnrolment);
    const { currentYear } = useAppSelector(state => state.pcs);
    const { updatePlannedActivityStatus } = useAppSelector(state => state.pcsPdp);

    useEffect(() => {
        if (enrolmentYears && enrolmentYears?.length > 0) {
            const enrolmentYear = enrolmentYears?.find(x => x.enrolmentYearId === enrolmentYearId);
            reset({
                [AccreditedContinuingEducationActivity.name]: enrolmentYear?.pdpExternal as string,
                [WorkBasedLearning.name]: enrolmentYear?.pdpWorkBasedLearning as string,
                [PracticeReview.name]: enrolmentYear?.pdpPracticeReview as string,
                [EngagedInPracticeOfMedicine.name]: (enrolmentYear?.engagedInPracticeOfMedicine || false).toString()
            });
        }
    }, [enrolmentYears, enrolmentYearId, reset]);

    useEffect(() => {
        if (enrolmentYears && enrolmentYears?.length > 0) {
            setEnrolmentYear(enrolmentYears?.find(x => x.enrolmentYearId === enrolmentYearId));
        }
    }, [enrolmentYears, enrolmentYearId]);

    useEffect(() => {
        if (enrolmentYears && enrolmentYears?.length > 0 && pcsPdpStatuses && currentYear) {
            const enrolmentYear = enrolmentYears?.find(x => x.enrolmentYearId === enrolmentYearId);
            const pcsPdpStatus = pcsPdpStatuses.find(x => x.id === enrolmentYear?.pcsPdpReviewStatusId);

            setCurrentEnrolmentYearStatus(pcsPdpStatus);
            setCanUpdatePlannedActivities(pcsPdpStatus?.code === PdpStatusEnum.Draft ||
                pcsPdpStatus?.code === PdpStatusEnum.NotStarted ||
                pcsPdpStatus?.code === PdpStatusEnum.Submitted ||
                pcsPdpStatus?.code === PdpStatusEnum.ReviewRequestCancelled ||
                pcsPdpStatus?.code === PdpStatusEnum.ReviewRequestDeclined ||
                pcsPdpStatus?.code === PdpStatusEnum.ReviewRequestInvalid);
        }
    }, [enrolmentYears, pcsPdpStatuses, currentYear, enrolmentYearId]);

    const savePlannedActivities = async (isSubmitted: boolean) => {
        const isStepValid = await trigger();
        if (!isStepValid) trackPortalEvent(portalScreenContext(PortalTelemetryScreen.UpdatePlannedActivities),
            PortalTelemetryEvent.ValidationBlocked, {
            step: undefined,
            validationErrors: validationIssues(PortalTelemetryScreen.UpdatePlannedActivities, methods.getFieldState)
        });

        if (isStepValid) {
            let plannedActivity = mapToPlannedActivitiesDto(isSubmitted);
            dispatch(updatePlannedActivityAsync(plannedActivity)).then((response: any) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    dispatch(getPcsEnrolmentYearsAsync()).then((response) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            toast.success(MESSAGE_DETAILS_SAVED);
                        }
                    });
                }
            });
        }
    };

    const mapToPlannedActivitiesDto = (isSubmitted: boolean) => {
        const data: PlannedActivity = {
            external: getValues([AccreditedContinuingEducationActivity.name]).toString(),
            workBasedLearning: getValues([WorkBasedLearning.name]).toString(),
            practiceReview: getValues([PracticeReview.name]).toString(),
            enrolmentYearId: enrolmentYearId,
            submit: isSubmitted
        }
        return data;
    }

    usePortalScreen(portalScreenContext(PortalTelemetryScreen.UpdatePlannedActivities), undefined, !((updatePlannedActivityStatus === ApiRequestStatus.Pending)));

    if (updatePlannedActivityStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <Box sx={{ width: '100%' }}>

        <Grid container rowSpacing={3}>

            <Grid size={{ xs: 7, sm: 3.5, lg: 2.5 }}>
                <Typography variant='h5' sx={{ fontWeight: '550' }}>Planned activities</Typography>
            </Grid>

            <Grid size={{ xs: 1, sm: 3, lg: 3 }} sx={{ marginTop: '-7px' }}>
                <AppInformationText information_text={'Outline activities that will assist you in meeting your CPD requirements.'} placement={'bottom-end'} />
            </Grid>

            <Grid size={{ xs: 4, sm: 5.5, lg: 6.5 }}></Grid>

            {canUpdatePlannedActivities === true && <>
                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={AccreditedContinuingEducationActivity.name}
                        label={AccreditedContinuingEducationActivity.label} maxStringCount={AccreditedContinuingEducationActivity.maxStringCount} />
                </Grid>
            </>}

            {canUpdatePlannedActivities === false && <>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <b>{AccreditedContinuingEducationActivity.label.trim().endsWith("*") ?
                        AccreditedContinuingEducationActivity.label.trim().substring(0, (AccreditedContinuingEducationActivity.label.trim().length - 1)) :
                        AccreditedContinuingEducationActivity.label}</b>
                </Grid>

                <Grid size={{ xs: 12, sm: 8.5 }}>
                    {enrolmentYear?.pdpExternal ? enrolmentYear.pdpExternal : ''}
                </Grid>
            </>}

            {canUpdatePlannedActivities === true && <>
                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={WorkBasedLearning.name} label={WorkBasedLearning.label}
                        disabled={!canUpdatePlannedActivities} maxStringCount={WorkBasedLearning.maxStringCount} />
                </Grid>
            </>}

            {canUpdatePlannedActivities === false && <>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <b>{WorkBasedLearning.label.trim().endsWith("*") ?
                        WorkBasedLearning.label.trim().substring(0, (WorkBasedLearning.label.trim().length - 1)) :
                        WorkBasedLearning.label}</b>
                </Grid>

                <Grid size={{ xs: 12, sm: 8.5 }}>
                    {enrolmentYear?.pdpWorkBasedLearning ? enrolmentYear.pdpWorkBasedLearning : ''}
                </Grid>
            </>}

            {canUpdatePlannedActivities === true && <>
                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={PracticeReview.name}
                        label={enrolmentYear?.engagedInPracticeOfMedicine === true ?
                            PracticeReview.label : PracticeReview.label.replace("*", "")}
                        disabled={!canUpdatePlannedActivities} maxStringCount={PracticeReview.maxStringCount} />
                </Grid>
            </>}

            {canUpdatePlannedActivities === false && <>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <b>{PracticeReview.label.trim().endsWith("*") ?
                        PracticeReview.label.trim().substring(0, (PracticeReview.label.trim().length - 1)) :
                        PracticeReview.label}</b>
                </Grid>

                <Grid size={{ xs: 12, sm: 8.5 }}>
                    {enrolmentYear?.pdpPracticeReview ? enrolmentYear.pdpPracticeReview : ''}
                </Grid>
            </>}

            {canUpdatePlannedActivities === true &&
                <Grid size={{ xs: 6, sm: 6, lg: 9 }}>
                    <Button variant="contained" color="success" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.UpdatePlannedActivities), `${PortalTelemetryScreen.UpdatePlannedActivities}.onClick.savePlannedActivities.submit`, () => savePlannedActivities(true))}>
                        Submit
                    </Button>
                </Grid>
            }

            {(currentEnrolmentYearStatus?.code === PdpStatusEnum.Draft ||
                currentEnrolmentYearStatus?.code === PdpStatusEnum.NotStarted) &&
                <Grid size={{ xs: 6, sm: 6, lg: 3 }}>
                    <Button variant="contained" color="warning" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.UpdatePlannedActivities), `${PortalTelemetryScreen.UpdatePlannedActivities}.onClick.savePlannedActivities.draft`, () => savePlannedActivities(false))}>
                        Save as a Draft
                    </Button>
                </Grid>}

        </Grid>
    </Box>
}
